// ============================================================================
// comprasIva.js — Cálculo de renglones, IVA y totales de una compra.
// ----------------------------------------------------------------------------
// Fuente única de verdad para el cálculo de la pantalla de Ingreso de Mercadería
// (ComprasNueva). Mantener acá la lógica permite testearla sin renderizar React.
//
// Dos modos:
//  - esRI = true  (Responsable Inscripto): la alícuota la define cada PRODUCTO.
//    El costo tipeado es NETO; el IVA es CRÉDITO FISCAL y NO se carga al costo.
//  - esRI = false (Monotributo / Exento / etc.): comportamiento histórico. El
//    IVA global se prorratea al costo según el peso de cada renglón.
// ============================================================================

import { afipIvaIdFromPercent, round2 } from './afipIva.js';

export const getUnidadesPorPaquete = (item) =>
  item?.por_paquete ? Math.max(1, Number(item.unidades_por_paquete) || 1) : 1;

export const getUnidadesTotales = (item) =>
  (Number(item?.cantidad) || 0) * getUnidadesPorPaquete(item);

export const getSubtotalItem = (item) =>
  (Number(item?.cantidad) || 0) * (Number(item?.costo) || 0);

// Calcula cada renglón: subtotal neto, IVA, unidades a stock y costo unitario.
// Para no-RI, `ivaProrrateableTotal` es el IVA global a distribuir al costo.
export const calcularItemsCompra = ({ items = [], esRI = false, ivaProrrateableTotal = 0 } = {}) => {
  const lista = Array.isArray(items) ? items : [];
  const subtotalRenglones = lista.reduce((acc, i) => acc + getSubtotalItem(i), 0);

  return lista.map((it) => {
    const sub = getSubtotalItem(it);
    const unidadesStock = getUnidadesTotales(it);
    const costoUnitarioNeto = unidadesStock > 0 ? sub / unidadesStock : 0;

    if (esRI) {
      // RI: IVA por producto, crédito fiscal (no entra al costo).
      const rate = (Number(it.iva_percentage) || 0) / 100;
      const ivaItem = round2(sub * rate);
      return {
        ...it,
        _subtotalNeto: sub,
        _ivaProrrateado: ivaItem,
        _unidadesStock: unidadesStock,
        _costoUnitarioNeto: costoUnitarioNeto,
        _costoUnitarioFinal: costoUnitarioNeto,
      };
    }

    // Flujo histórico: IVA global prorrateado al costo según el peso del renglón.
    const peso = subtotalRenglones > 0 ? sub / subtotalRenglones : 0;
    const ivaItem = peso * ivaProrrateableTotal;
    const costoUnitarioFinal = unidadesStock > 0 ? (sub + ivaItem) / unidadesStock : 0;
    return {
      ...it,
      _subtotalNeto: sub,
      _ivaProrrateado: ivaItem,
      _unidadesStock: unidadesStock,
      _costoUnitarioNeto: costoUnitarioNeto,
      _costoUnitarioFinal: costoUnitarioFinal,
    };
  });
};

// IVA total (RI): suma del IVA de cada renglón.
export const ivaTotalPorProducto = (itemsCalculados = []) =>
  round2((itemsCalculados || []).reduce((acc, i) => acc + (Number(i._ivaProrrateado) || 0), 0));

// Desglose por alícuota para el Libro IVA Compras (un grupo por tasa).
// La tasa 0% representa operaciones exentas (base sin IVA).
export const construirIvaBreakdown = (itemsCalculados = []) => {
  const byPct = new Map();
  for (const it of (itemsCalculados || [])) {
    const pct = Number(it.iva_percentage) || 0;
    const base = Number(it._subtotalNeto) || 0;
    const iva = Number(it._ivaProrrateado) || 0;
    if (base <= 0 && iva <= 0) continue;
    const cur = byPct.get(pct) || { percent: pct, base: 0, iva: 0 };
    cur.base += base;
    cur.iva += iva;
    byPct.set(pct, cur);
  }
  return Array.from(byPct.values()).map((g) => ({
    percent: g.percent,
    Id: afipIvaIdFromPercent(g.percent),
    base: round2(g.base),
    iva: round2(g.iva),
  }));
};
